import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getLedger } from '../../actions/canTransaction';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Table, 
    Badge, 
    Alert, 
    Pagination,
    Spinner,
    Toast,
    ToastContainer
} from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css"

/**
 * Ledger Component with Enhanced URL Query Parameter Support
 *
 * This component now supports extracting all query parameters from the URL:
 * - user: Filter by user ID
 * - product: Filter by product ID
 * - startDate: Filter by start date (YYYY-MM-DD)
 * - endDate: Filter by end date (YYYY-MM-DD)
 * - latest: Sort order (true/false)
 * - page: Page number for pagination
 *
 * Example URLs:
 * - /ledger?user=1234567890
 * - /ledger?product=prod123&startDate=2024-01-01&endDate=2024-01-31
 * - /ledger?user=user123&latest=false&page=2
 */
const Ledger = ({ledger, products, getLedger, pageList, pages, activePage, loading}) => {
    // Get location from React Router v6 hook
    const location = useLocation();

    const initialState = {
        product:'',
        startDate:'',
        endDate:'',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([])
    const [pageCount, setPage] = useState(0);
    const [currentPage, setActivePage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [user, setUser] = useState("");
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');

    const showAlert = (message, variant = 'danger') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    // Function to extract query parameters from URL
    const getQueryParams = (search) => {
        const params = new URLSearchParams(search);
        const queryParams = {};
        // Extract all query parameters
        for (const [key, value] of params.entries()) {
            queryParams[key] = value;
        }
        return queryParams;
    };

    // Function to build query string from parameters
    const buildQueryString = (params) => {
        const queryParts = [];
        // Add default parameters
        queryParts.push(`page=${params.page || 1}`);
        queryParts.push(`latest=${params.latest || true}`);
        // Add other parameters if they exist
        if (params.user) queryParts.push(`user=${params.user}`);
        if (params.product) queryParts.push(`product=${params.product}`);
        if (params.startDate) queryParts.push(`startDate=${params.startDate}`);
        if (params.endDate) queryParts.push(`endDate=${params.endDate}`);
        return queryParts.join('&');
    };

    useEffect(() => {
        if(ledger === undefined){
            let query = queryString;
            if(location && location.search){
                // Extract all query parameters from URL
                const urlParams = getQueryParams(location.search);
                // Update form data with URL parameters
                const updatedFormData = {
                    ...formData,
                    product: urlParams.product || '',
                    startDate: urlParams.startDate || '',
                    endDate: urlParams.endDate || '',
                    latest: urlParams.latest !== undefined ? urlParams.latest === 'true' : true
                };
                setFormData(updatedFormData);
                // Set specific user ID if user parameter exists
                if (urlParams.user) {
                    setUser(urlParams.user);
                }
                // Build query string from URL parameters
                query = buildQueryString(urlParams);
                setQueryString(query);
            }
            getLedger(query);
        }else{
            let ledgerData = [];
            ledger.forEach(ledgerObj => {                
                ledgerData.push({
                    transaction: ledgerObj.transaction ? ledgerObj.transaction.transactionId : ledgerObj.order ? ledgerObj.order.orderId : ledgerObj.canReturn ? ledgerObj.canReturn.transactionId : "",
                    type: ledgerObj.transaction ? "transaction" : ledgerObj.order ? "order" : ledgerObj.canReturn ? "can_return" : "",
                    transactionId : ledgerObj.transaction ? ledgerObj.transaction._id : ledgerObj.order ? ledgerObj.order._id : ledgerObj.canReturn ? ledgerObj.canReturn._id : "",
                    time: new Date(ledgerObj.time).toString().split("GMT")[0],
                    brand: ledgerObj.product?.brand || "",
                    credit: ledgerObj.credit,
                    debit: ledgerObj.debit,
                    total: ledgerObj.total,
                    message: ledgerObj.message,
                    user: ledgerObj.user?.name || "Deleted User", // Check if user is deleted
                    userId: ledgerObj.user?._id || "N/A", // Check if user is deleted
                    freeCans: ledgerObj.freeCans || 0
                });
            });
            setQueryString(queryString.replace("page=" + currentPage, "page=" + activePage));
            setData(ledgerData);
            setPage(pages);
            setActivePage(activePage);
        }
    }, [ ledger, products ] );

    const {
        product,
        startDate,
        endDate,
        latest
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        
        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate).getTime();
            const endDate = new Date(formData.endDate).getTime();
            if(endDate < startDate){
                showAlert("End date must be after start date");
                return;
            }
        }
        
        // Build query parameters object
        const queryParams = {
            page: 1,
            latest: formData.latest,
            product: formData.product || undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            user: user || undefined
        };
        // Build query string from parameters
        const newQueryString = buildQueryString(queryParams);
        setQueryString(newQueryString);
        getLedger(newQueryString);
        showAlert("Filters applied successfully!", "success");
    };

    const handleSort = (field) => {
        const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(direction);
        
        const sortedData = [...data].sort((a, b) => {
            if (direction === 'asc') {
                return a[field] > b[field] ? 1 : -1;
            }
            return a[field] < b[field] ? 1 : -1;
        });
        setData(sortedData);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1"></i>
            : <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageCount) {
            const newQueryString = queryString.replace(`page=${currentPage}`, `page=${page}`);
            setQueryString(newQueryString);
            setActivePage(page);
            getLedger(newQueryString);
        }
    };

    const renderPagination = () => {
        if (pageCount <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pageCount, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }

        // Page numbers
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pageCount) {
            if (endPage < pageCount - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            items.push(
                <Pagination.Item key={pageCount} onClick={() => handlePageChange(pageCount)}>
                    {pageCount}
                </Pagination.Item>
            );
        }

        return (
            <div className="mt-3">
                <div className="text-center text-muted mb-2">
                    Page {currentPage} of {pageCount}
                </div>
                <div className="d-flex justify-content-center" style={{marginBottom: "10px"}}>
                    <Pagination className="mb-0">
                        <Pagination.Prev 
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {items}
                        <Pagination.Next 
                            disabled={currentPage === pageCount}
                            onClick={() => handlePageChange(currentPage + 1)}
                        />
                    </Pagination>
                </div>
            </div>
        );
    };

    const showProducts = () => {
        return products.map((product, index) => {
            return (
                <option key={`product-${index}`} value={product._id}>{product.brand}</option>
            )
        });
    }

    const clearFilters = () => {
        setFormData(initialState);
        const queryParams = {
            page: 1,
            latest: true,
            user: user || undefined
        };
        const newQueryString = buildQueryString(queryParams);
        setQueryString(newQueryString);
        getLedger(newQueryString);
        showAlert("Filters cleared successfully!", "info");
    };

    const renderLedgerTable = () => {
        if (!data || data.length === 0) {
            return (
                <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No ledger data found. Try adjusting your filters.
                </Alert>
            );
        }

        return (
            <>
                <div className="table-responsive">
                    <Table striped bordered hover className="mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('transaction')}
                                >
                                    Transaction / Order
                                    {getSortIcon('transaction')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('user')}
                                >
                                    User
                                    {getSortIcon('user')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('brand')}
                                >
                                    Product
                                    {getSortIcon('brand')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('credit')}
                                >
                                    Credit
                                    {getSortIcon('credit')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('debit')}
                                >
                                    Debit
                                    {getSortIcon('debit')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('total')}
                                >
                                    Total
                                    {getSortIcon('total')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('freeCans')}
                                >
                                    Free Cans
                                    {getSortIcon('freeCans')}
                                </th>
                                <th 
                                    className="cursor-pointer user-select-none"
                                    onClick={() => handleSort('time')}
                                >
                                    Time
                                    {getSortIcon('time')}
                                </th>
                                <th>Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => (
                                <tr key={index}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Badge
                                                bg={row.type === 'transaction' ? 'primary' : row.type === 'can_return' ? 'info' : 'success'}
                                                className="me-2"
                                            >
                                                {row.type === 'transaction' ? 'T' : row.type === 'can_return' ? 'R' : 'O'}
                                            </Badge>
                                            <span className="text-truncate" style={{maxWidth: '150px'}} title={row.transaction}>
                                                {row.transaction}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span>{row.user}</span>
                                    </td>
                                    <td>
                                        <Badge bg="secondary">{row.brand}</Badge>
                                    </td>
                                                                         <td>
                                         {row.credit > 0 && (
                                             <span className="text-success fw-bold">
                                                 <i className="fas fa-plus me-1"></i>
                                                 {row.credit}
                                             </span>
                                         )}
                                     </td>
                                     <td>
                                         {row.debit > 0 && (
                                             <span className="text-danger fw-bold">
                                                 <i className="fas fa-minus me-1"></i>
                                                 {row.debit}
                                             </span>
                                         )}
                                     </td>
                                     <td>
                                        <span className={`fw-bold ${row.total >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {row.total}
                                        </span>
                                    </td>
                                    <td>
                                        {row.freeCans > 0 ? (
                                            <Badge bg="success">
                                                <i className="fas fa-gift me-1"></i>
                                                {row.freeCans}
                                            </Badge>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {new Date(row.time).toLocaleDateString()}<br/>
                                            {new Date(row.time).toLocaleTimeString()}
                                        </small>
                                    </td>
                                    <td>
                                        <span className=" d-inline-block" style={{maxWidth: '200px'}} title={row.message}>
                                            {row.message}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                {renderPagination()}
            </>
        );
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-primary mb-0">
                    <i className="fas fa-book me-2"></i>
                    Can Ledger
                </h2>
                {user && (
                    <Badge bg="info">
                        <i className="fas fa-user me-1"></i>
                        User Specific View
                    </Badge>
                )}
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-box me-2"></i>
                                        Product
                                    </Form.Label>
                                    <Form.Select
                                        name="product"
                                        value={product}
                                        onChange={onChange}
                                    >
                                        <option value="">All Products</option>
                                        {showProducts()}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={latest}
                                        onChange={onChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={4} className="d-flex align-items-end">
                                <div className="d-flex gap-2 w-100">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex-fill"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-search me-2"></i>
                                                Apply Filters
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Results Card */}
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-table me-2"></i>
                        Ledger Records
                    </h5>
                    {data.length > 0 && (
                        <Badge bg="light" text="dark">
                            {data.length} record{data.length !== 1 ? 's' : ''}
                        </Badge>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading ledger data...</div>
                        </div>
                    ) : (
                        renderLedgerTable()
                    )}
                </Card.Body>
            </Card>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : toastVariant === 'info' ? 'Info' : 'Alert'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? '' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <style>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .user-select-none {
                    user-select: none;
                }
                .table th:hover {
                    background-color: #495057 !important;
                }
            `}</style>
        </Container>
    )
}

Ledger.propTypes = {
    getLedger : PropTypes.func.isRequired,
    ledger : PropTypes.array,
    pages : PropTypes.number,
    activePage : PropTypes.number,
    loading: PropTypes.bool,
    products: PropTypes.array,
    pageList: PropTypes.array
}

const mapStateToProps = state => ({
    ledger: state.canTransaction.ledger,
    products: state.canTransaction.products,
    pages: state.canTransaction.pages,
    pageList: state.canTransaction.pageList,
    activePage: state.canTransaction.activePage,
    loading: state.canTransaction.loading
})

export default connect( mapStateToProps, { getLedger } )( Ledger );